import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowRightLeft, Search, X, Plus, Trash2, Inbox,
  Loader2, Save, Send, PackageCheck, ChevronDown,
  AlertTriangle, CheckCircle2, Printer
} from 'lucide-react';
import { SidePanel } from '../components/SidePanel';
import * as api from '../api';
import { toast } from 'sonner';
import { useSociete } from '../context/SocieteContext';

// ─── Styles communs panels ────────────────────────────────────────────────────
const TH_STYLE = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' };
const TD_STYLE = { padding: '10px 12px', fontSize: 13 };

// ─── Constantes ───────────────────────────────────────────────────────────────

const STATUTS = [
  { key: 'demande',      label: 'Demande',       bg: '#F1F5F9', color: '#475569', border: '#CBD5E1', dot: '#94A3B8' },
  { key: 'expedie',      label: 'Expédié',        bg: '#FFF7ED', color: '#B45309', border: '#FCD34D', dot: '#F59E0B' },
  { key: 'receptionne',  label: 'Réceptionné',   bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0', dot: '#22C55E' },
  { key: 'annule',       label: 'Annulé',         bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA', dot: '#EF4444' },
];

function getStatut(key) { return STATUTS.find(s => s.key === key) || STATUTS[0]; }

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
}

function StatutBadge({ statut }) {
  const s = getStatut(statut);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

// ─── ArticleCombobox ──────────────────────────────────────────────────────────

function ArticleCombobox({ value, onChange, placeholder = 'Rechercher un article…' }) {
  const [query, setQuery]     = useState(value?.article_designation || '');
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.getArticles({ q: query, limit: 20 });
        setResults(data?.items || data || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
  }, [query]);

  function select(art) {
    setQuery(art.designation || '');
    setOpen(false);
    onChange({
      article_id:          art.article_id,
      article_ref:         art.reference || '',
      article_designation: art.designation || '',
      article_type:        art.type || '',
    });
  }

  return (
    <div className="relative">
      <input
        className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        value={query}
        placeholder={placeholder}
        onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(null); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && (query.length >= 2) && (
        <div className="absolute z-50 bg-white border rounded shadow-lg mt-1 w-full max-h-48 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-gray-400">Chargement…</div>}
          {!loading && results.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">Aucun résultat</div>}
          {results.map(art => (
            <button key={art.article_id} className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm" onMouseDown={() => select(art)}>
              <div className="font-medium">{art.designation}</div>
              <div className="text-xs text-gray-500">{art.reference} · {art.type}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ZoneSelect ───────────────────────────────────────────────────────────────

function ZoneSelect({ zones, value, onChange, placeholder = 'Zone (optionnel)' }) {
  return (
    <select
      className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
      value={value || ''}
      onChange={e => onChange(e.target.value || null)}
    >
      <option value="">{placeholder}</option>
      {zones.map(z => (
        <option key={z.zone_id} value={z.zone_id}>{z.nom}</option>
      ))}
    </select>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function Transferts() {
  const { activeAgences, loading: agencesLoading } = useSociete();
  const [searchParams, setSearchParams] = useSearchParams();

  const [transferts, setTransferts]     = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [search, setSearch]             = useState('');
  const [filterPill, setFilterPill]     = useState(''); // '' | 'expedition' | 'reception'
  const [selectedId, setSelectedId]     = useState(searchParams.get('id') || null);
  const [selectedMode, setSelectedMode] = useState('ouvrir');
  const [showCreate, setShowCreate]     = useState(false);

  const agenceId  = activeAgences?.length === 1 ? activeAgences[0].agence_id : null;
  const agenceNom = activeAgences?.length === 1 ? activeAgences[0].nom : null;

  // Mapping pill → statut backend
  const filterStatut = filterPill === 'expedition' ? 'demande' : filterPill === 'reception' ? 'expedie' : '';

  // ── Chargement liste ──────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50, offset: 0 };
      if (search)       params.search = search;
      if (filterStatut) params.statut = filterStatut;
      if (agenceId)     params.agence_source_id = agenceId;
      const { data } = await api.getTransferts(params);
      let items = data?.items || [];
      if (agenceId) {
        const { data: d2 } = await api.getTransferts({ ...params, agence_source_id: undefined, agence_dest_id: agenceId });
        const ids = new Set(items.map(i => i.transfert_id));
        (d2?.items || []).forEach(i => { if (!ids.has(i.transfert_id)) items.push(i); });
        items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      }
      setTransferts(items);
      setTotal(data?.total || items.length);
    } catch { toast.error('Erreur chargement transferts'); }
    setLoading(false);
  }, [search, filterStatut, agenceId]);

  useEffect(() => { load(); }, [load]);

  function select(id, mode = 'ouvrir') {
    setSelectedId(id);
    setSelectedMode(mode);
    setSearchParams(id ? { id } : {});
    setShowCreate(false);
  }

  const PILLS = [
    { key: '',           label: 'Tous' },
    { key: 'expedition', label: 'Expédition' },
    { key: 'reception',  label: 'Réception' },
  ];

  // ── UI ────────────────────────────────────────────────────────────────────
  return (
    <div data-testid="transferts-page" style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#F8FAFC' }}>

      {/* En-tête */}
      <div style={{ padding: '20px 24px 0' }}>
        <h1 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.01em' }}>
          TRANSFERTS INTER-AGENCES
          {agenceNom && (
            <span style={{ fontWeight: 400, color: '#64748B', fontSize: 16 }}>
              {' '}|{' '}Agence : {agenceNom}
            </span>
          )}
        </h1>

        {/* Barre recherche + pills + bouton */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '0 0 220px' }}>
            <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
            <input
              placeholder="Rechercher…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', paddingLeft: 28, paddingRight: 8, paddingTop: 7, paddingBottom: 7, border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, background: '#fff', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {/* Pills filtre */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 12, color: '#94A3B8', marginRight: 4, whiteSpace: 'nowrap' }}>Filtres :</span>
            {PILLS.map(p => (
              <button
                key={p.key}
                onClick={() => setFilterPill(p.key)}
                style={{
                  padding: '5px 14px', borderRadius: 8, fontSize: 13, fontWeight: filterPill === p.key ? 700 : 500,
                  cursor: 'pointer', border: filterPill === p.key ? '2px solid #2563EB' : '1px solid #E2E8F0',
                  background: filterPill === p.key ? '#EFF6FF' : '#fff',
                  color: filterPill === p.key ? '#2563EB' : '#374151',
                  transition: 'all .1s',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Bouton nouveau — uniquement si une agence active */}
          {activeAgences?.length === 1 && (
            <button
              data-testid="btn-nouveau-transfert"
              onClick={() => { setShowCreate(true); setSelectedId(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#16A34A', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              <Plus size={15} /> Nouvelle Demande
            </button>
          )}
        </div>
      </div>

      {/* Tableau */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
        {(loading || agencesLoading) && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <Loader2 size={22} className="animate-spin" style={{ color: '#94A3B8' }} />
          </div>
        )}
        {!loading && !agencesLoading && transferts.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 80, color: '#94A3B8' }}>
            <Inbox size={40} style={{ marginBottom: 10, opacity: 0.4 }} />
            <p style={{ margin: 0, fontSize: 14 }}>Aucun transfert</p>
          </div>
        )}
        {!loading && !agencesLoading && transferts.length > 0 && (
          <TransfertsTable
            transferts={transferts}
            selectedId={selectedId}
            activeAgences={activeAgences}
            onSelect={select}
          />
        )}
      </div>

      {/* Panneau détail */}
      {selectedId && (
        <SidePanel open={!!selectedId} onOpenChange={open => { if (!open) select(null); }} onPointerDownOutside={() => select(null)} width={840}>
          <TransfertDetail
            transfertId={selectedId}
            mode={selectedMode}
            onClose={() => select(null)}
            onRefresh={() => { load(); }}
          />
        </SidePanel>
      )}

        {showCreate && (
        <SidePanel open={showCreate} onOpenChange={open => { if (!open) setShowCreate(false); }} onPointerDownOutside={() => setShowCreate(false)} width={680}>
          <TransfertCreateForm
            activeAgence={activeAgences[0]}
            onClose={() => setShowCreate(false)}
            onCreated={id => { setShowCreate(false); select(id); load(); }}
          />
        </SidePanel>
      )}
    </div>
  );
}

// ─── Logique narrative ────────────────────────────────────────────────────────

const BORDER_COLORS = {
  expedier:     '#2563EB',
  receptionner: '#F59E0B',
  expedie:      '#F59E0B',
  attente:      '#7C3AED',
  ouvrir:       '#16A34A',
  annule:       '#CBD5E1',
};

const STATUS_INFO = {
  expedier:     { dot: '#2563EB', label: 'À préparer' },
  receptionner: { dot: '#F59E0B', label: 'Expédié' },
  expedie:      { dot: '#F59E0B', label: 'Expédié' },
  attente:      { dot: '#7C3AED', label: 'En attente' },
  ouvrir:       { dot: '#16A34A', label: 'Terminé' },
  annule:       { dot: '#94A3B8', label: 'Annulé' },
};

function getNarrative(t, activeAgences) {
  const activeIds = new Set((activeAgences || []).map(a => a.agence_id));
  const isSource = activeIds.has(t.agence_source_id);
  const isDest   = activeIds.has(t.agence_dest_id);
  const isBoth   = isSource && isDest;
  const s = t.statut;
  const src = t.agence_source_nom || '—';
  const dst = t.agence_dest_nom   || '—';

  let agence, action, bouton, date;

  if (isBoth) {
    agence = src;
    if      (s === 'demande')     { action = `a demandé à ${dst}`;   bouton = 'expedier'; }
    else if (s === 'expedie')     { action = `a expédié à ${dst}`;   bouton = 'receptionner'; }
    else if (s === 'receptionne') { action = `a envoyé à ${dst}`;    bouton = 'ouvrir'; }
    else                          { action = `a annulé vers ${dst}`;  bouton = 'annule'; }
  } else if (isSource) {
    if      (s === 'demande')     { agence = dst;    action = 'vous demande';             bouton = 'expedier'; }
    else if (s === 'expedie')     { agence = 'Vous'; action = `avez expédié à ${dst}`;   bouton = null; }
    else if (s === 'receptionne') { agence = 'Vous'; action = `avez envoyé à ${dst}`;    bouton = 'ouvrir'; }
    else                          { agence = 'Vous'; action = `aviez initié vers ${dst}`; bouton = 'annule'; }
  } else if (isDest) {
    if      (s === 'demande')     { agence = 'Vous'; action = `avez demandé à ${src}`;   bouton = null; }
    else if (s === 'expedie')     { agence = src;    action = 'vous a envoyé';            bouton = 'receptionner'; }
    else if (s === 'receptionne') { agence = 'Vous'; action = `avez reçu de ${src}`;     bouton = 'ouvrir'; }
    else                          { agence = src;    action = 'a annulé';                bouton = 'annule'; }
  } else {
    agence = src; action = `→ ${dst}`; bouton = 'ouvrir';
  }

  if      (s === 'expedie')     date = t.date_expedition;
  else if (s === 'receptionne') date = t.date_reception;
  else                          date = t.date_demande || t.created_at;

  // Statut visuel
  let statusKey;
  if (s === 'annule')           statusKey = 'annule';
  else if (s === 'receptionne') statusKey = 'ouvrir';
  else if (bouton === 'expedier')     statusKey = 'expedier';
  else if (s === 'expedie')     statusKey = 'expedie';
  else statusKey = 'attente';

  const borderColor  = BORDER_COLORS[statusKey] || '#E2E8F0';
  const statusInfo   = STATUS_INFO[statusKey]  || { dot: '#94A3B8', label: s };

  return { agence, action, bouton, date, borderColor, statusInfo };
}

// ─── Tableau principal ────────────────────────────────────────────────────────

const HEADERS = [
  { label: 'Numéro',     width: 150 },
  { label: 'Agence',     width: 150 },
  { label: 'Action',     width: 300 },
  { label: 'Articles',   width: null },
  { label: 'Date',       width: 100 },
  { label: 'Statut',     width: 150 },
  { label: 'Opération',  width: 150 },
];

function TransfertsTable({ transferts, selectedId, activeAgences, onSelect }) {
  if (transferts.length === 0) return null;
  return (
    <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,.07), 0 0 0 1px #E2E8F0', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          {HEADERS.map((h, i) => (
            <col key={i} style={{ width: h.width || undefined }} />
          ))}
        </colgroup>
        <thead>
          <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E8EDF2' }}>
            {HEADERS.map((h, i) => (
              <th key={i} style={{ padding: '10px 14px', textAlign: 'center', verticalAlign: 'bottom', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, color: '#374151' }}>
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {transferts.map(t => (
            <TransfertTableRow
              key={t.transfert_id}
              transfert={t}
              selected={selectedId === t.transfert_id}
              activeAgences={activeAgences}
              onSelect={onSelect}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TransfertTableRow({ transfert: t, selected, activeAgences, onSelect }) {
  const { agence, action, bouton, date, borderColor, statusInfo } = getNarrative(t, activeAgences);
  const lignes = Array.isArray(t.lignes) ? t.lignes : [];

  const BTN_STYLES = {
    expedier:     { bg: '#2563EB', color: '#fff',    label: 'Expédier' },
    receptionner: { bg: '#F59E0B', color: '#fff',    label: 'Réceptionner' },
    ouvrir:       { bg: '#F1F5F9', color: '#475569', label: 'Ouvrir' },
  };
  const btn = bouton && BTN_STYLES[bouton] ? BTN_STYLES[bouton] : null;

  const rowBg = selected ? '#EFF6FF' : 'transparent';

  return (
    <tr
      data-testid={`transfert-row-${t.transfert_id}`}
      onClick={() => onSelect(t.transfert_id, 'ouvrir')}
      style={{ cursor: 'pointer', background: rowBg, transition: 'background .1s', borderBottom: '1px solid #F1F5F9' }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#FAFAFA'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Numéro */}
      <td style={{ padding: '12px 14px', fontSize: 13, whiteSpace: 'nowrap', textAlign: 'center' }}>
        {t.numero}
      </td>

      {/* Agence */}
      <td style={{ padding: '12px 14px', fontSize: 13, whiteSpace: 'nowrap', textAlign: 'center' }}>
        {agence}
      </td>

      {/* Action */}
      <td style={{ padding: '12px 14px', fontSize: 13, textAlign: 'center' }}>
        {action}
      </td>

      {/* Articles */}
      <td style={{ padding: '8px 14px' }}>
        {lignes.length === 0 ? '—' : (
          <div style={{ fontSize: 12, lineHeight: '1.6' }}>
            {lignes.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 25 }}>
                <span>{l.quantite_envoyee || l.quantite_demandee || '?'} x</span>
                <span>{l.article_ref}</span>
                <span>{l.article_designation}</span>
              </div>
            ))}
          </div>
        )}
      </td>

      {/* Date */}
      <td style={{ padding: '12px 14px', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'center' }}>
        {date ? new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
      </td>

      {/* Statut — point + texte */}
      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap', textAlign: 'center' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: statusInfo.dot, flexShrink: 0, display: 'inline-block' }} />
          {statusInfo.label}
        </span>
      </td>

      {/* Opération */}
      <td style={{ padding: '12px 14px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
        {btn && (
          <button
            onClick={() => onSelect(t.transfert_id, bouton || 'ouvrir')}
            style={{
              padding: '5px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', border: 'none',
              background: btn.bg, color: btn.color,
              whiteSpace: 'nowrap', letterSpacing: '.01em',
            }}
          >
            {btn.label}
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── Panneau détail ───────────────────────────────────────────────────────────

function TransfertDetail({ transfertId, mode = 'ouvrir', onClose, onRefresh }) {
  const [doc, setDoc]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [zonesSource, setZonesSource] = useState([]);
  const [zonesDest, setZonesDest]     = useState([]);
  // Pour l'expédition / réception
  const [ligneEdits, setLigneEdits]   = useState({});
  // Matériels disponibles par ligne (pour sélection N° série à l'expédition)
  const [materielOptions, setMaterielOptions] = useState({});
  // État mode édition (demande modifiable)
  const [editMode, setEditMode]       = useState(false);
  const [editForm, setEditForm]       = useState({});
  const [editLignes, setEditLignes]   = useState([]);
  const [agences, setAgences]         = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setEditMode(false);
    try {
      const { data } = await api.getTransfert(transfertId);
      setDoc(data);
      // Charger les zones
      if (data.stock_source_id) {
        try {
          const { data: zs } = await api.getZones(data.stock_source_id);
          setZonesSource(zs || []);
        } catch { setZonesSource([]); }
      }
      if (data.stock_dest_id) {
        try {
          const { data: zd } = await api.getZones(data.stock_dest_id);
          setZonesDest(zd || []);
        } catch { setZonesDest([]); }
      }
      // Initialiser les éditions de lignes
      const edits = {};
      (data.lignes || []).forEach(l => {
        edits[l.ligne_id] = {
          quantite_envoyee: l.quantite_envoyee || l.quantite_demandee,
          quantite_recue:   l.quantite_recue   || l.quantite_envoyee || l.quantite_demandee,
          zone_dest_id:     l.zone_dest_id     || null,
        };
      });
      setLigneEdits(edits);
    } catch { toast.error('Erreur chargement transfert'); }
    setLoading(false);
  }, [transfertId]);

  useEffect(() => { load(); }, [load]);

  // Charger les matériels disponibles pour les lignes de type 'materiel' (mode expédition)
  useEffect(() => {
    if (!doc || mode !== 'expedier' || doc.statut !== 'demande') return;
    const materielLignes = (doc.lignes || []).filter(l => l.article_type === 'materiel' && l.article_id);
    if (!materielLignes.length) return;
    materielLignes.forEach(async l => {
      try {
        const { data } = await api.getMateriels({ article_id: l.article_id, agence_id: doc.agence_source_id, slim: true });
        const list = Array.isArray(data) ? data : (data?.items || []);
        setMaterielOptions(prev => ({ ...prev, [l.ligne_id]: list }));
        // Pré-sélectionner si le matériel était déjà renseigné
        if (l.materiel_id) {
          setLigneEdits(prev => ({ ...prev, [l.ligne_id]: { ...prev[l.ligne_id], materiel_id: l.materiel_id } }));
        }
      } catch { setMaterielOptions(prev => ({ ...prev, [l.ligne_id]: [] })); }
    });
  }, [doc, mode]);

  // ── Expédier ──────────────────────────────────────────────────────────────
  async function handleExpedier() {
    if (!doc) return;
    setSaving(true);
    try {
      const lignes = doc.lignes.map(l => ({
        ligne_id:         l.ligne_id,
        quantite_envoyee: parseFloat(ligneEdits[l.ligne_id]?.quantite_envoyee || 0),
        materiel_id:      ligneEdits[l.ligne_id]?.materiel_id || null,
      })).filter(l => l.quantite_envoyee > 0);
      if (!lignes.length) { toast.error('Renseignez au moins une quantité envoyée'); setSaving(false); return; }
      const { data } = await api.expedierTransfert(doc.transfert_id, { lignes });
      setDoc(data);
      onRefresh();
      toast.success('Transfert expédié');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur expédition'); }
    setSaving(false);
  }

  // ── Réceptionner ──────────────────────────────────────────────────────────
  async function handleReceptionner() {
    if (!doc) return;
    setSaving(true);
    try {
      const lignes = doc.lignes.map(l => ({
        ligne_id:       l.ligne_id,
        quantite_recue: parseFloat(ligneEdits[l.ligne_id]?.quantite_recue || 0),
      })).filter(l => l.quantite_recue > 0);
      if (!lignes.length) { toast.error('Renseignez au moins une quantité reçue'); setSaving(false); return; }
      const { data } = await api.receptionnerTransfert(doc.transfert_id, { lignes });
      setDoc(data);
      onRefresh();
      toast.success('Transfert réceptionné');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur réception'); }
    setSaving(false);
  }

  // ── Annuler ───────────────────────────────────────────────────────────────
  async function handleAnnuler() {
    if (!window.confirm('Confirmer l\'annulation ?')) return;
    setSaving(true);
    try {
      await api.annulerTransfert(doc.transfert_id);
      onRefresh(); onClose();
      toast.success('Transfert annulé');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur annulation'); }
    setSaving(false);
  }

  // ── Imprimer bordereau de transfert ──────────────────────────────────────
  async function handlePrint() {
    if (!doc) return;
    const lignes = doc.lignes || [];
    let srcAgence = {}, dstAgence = {};
    try {
      const { data: ag } = await api.getAgences();
      const list = Array.isArray(ag) ? ag : (ag?.items || []);
      srcAgence = list.find(a => a.agence_id === doc.agence_source_id) || {};
      dstAgence = list.find(a => a.agence_id === doc.agence_dest_id) || {};
    } catch {}
    const total = lignes.reduce((s, l) => {
      const qty = parseFloat(l.quantite_envoyee || l.quantite_demandee || 0);
      const pmp = parseFloat(l.pmp_source || l.article_prix_achat || 0);
      return s + qty * pmp;
    }, 0);
    const addr = (a) => [a.adresse, [a.code_postal, a.ville].filter(Boolean).join(' ')].filter(Boolean).join('<br>');
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8">
      <title>Bordereau de transfert ${doc.numero}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Segoe UI',Arial,sans-serif;font-size:12px;color:#111827;background:#fff;padding:40px 48px}
        .top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:36px}
        .emitter img{max-height:52px;max-width:160px;display:block;margin-bottom:10px}
        .emitter-name{font-size:15px;font-weight:700;color:#111827;margin-bottom:2px}
        .emitter-addr{font-size:11px;color:#6b7280;line-height:1.6}
        .doc-meta{text-align:right}
        .doc-title{font-size:22px;font-weight:800;color:#111827;letter-spacing:-.3px;margin-bottom:6px}
        .doc-num{font-size:13px;color:#6b7280}
        .doc-date{font-size:12px;color:#6b7280;margin-top:2px}
        .dest-block{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:14px 18px;margin-bottom:32px;display:inline-block;min-width:220px}
        .dest-label{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#9ca3af;margin-bottom:6px}
        .dest-name{font-size:13px;font-weight:700;color:#111827;margin-bottom:2px}
        .dest-addr{font-size:11px;color:#6b7280;line-height:1.6}
        table{width:100%;border-collapse:collapse;margin-top:4px}
        thead tr{border-bottom:2px solid #111827}
        th{padding:9px 12px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;text-align:left}
        th.r{text-align:right}
        tbody tr{border-bottom:1px solid #f3f4f6}
        tbody tr:nth-child(even){background:#f9fafb}
        td{padding:9px 12px;font-size:12px;color:#374151}
        td.num{text-align:right;font-variant-numeric:tabular-nums}
        td.ref{color:#9ca3af;font-size:11px}
        .total-row{border-top:2px solid #111827!important;background:#fff!important}
        .total-row td{padding:11px 12px;font-weight:700;font-size:13px}
      </style>
    </head><body>
      <div class="top">
        <div class="emitter">
          ${srcAgence.logo_b64 ? `<img src="${srcAgence.logo_b64}">` : ''}
          <div class="emitter-name">${srcAgence.nom || '—'}</div>
          <div class="emitter-addr">${addr(srcAgence)}</div>
          ${srcAgence.telephone ? `<div class="emitter-addr" style="margin-top:2px">Tél. ${srcAgence.telephone}</div>` : ''}
        </div>
        <div class="doc-meta">
          <div class="doc-title">Bordereau de transfert</div>
          <div class="doc-num">${doc.numero}</div>
          ${doc.date_expedition ? `<div class="doc-date">Expédié le ${fmtDate(doc.date_expedition)}</div>` : ''}
        </div>
      </div>
      <div style="display:flex;justify-content:flex-end;margin-bottom:32px">
      <div class="dest-block">
        <div class="dest-label">Destinataire</div>
        <div class="dest-name">${dstAgence.nom || doc.agence_dest_nom || '—'}</div>
        <div class="dest-addr">${addr(dstAgence)}</div>
      </div></div>
      <table>
        <thead>
          <tr>
            <th style="width:55px">Qté</th>
            <th style="width:110px">Référence</th>
            <th>Désignation</th>
            <th class="r" style="width:95px">PMP (€)</th>
            <th class="r" style="width:110px">Montant (€)</th>
          </tr>
        </thead>
        <tbody>
          ${lignes.map(l => {
            const qty = parseFloat(l.quantite_envoyee || l.quantite_demandee || 0);
            const pmp = parseFloat(l.pmp_source || l.article_prix_achat || 0);
            const montant = qty * pmp;
            return `<tr>
              <td>${qty}</td>
              <td class="ref">${l.article_ref || '—'}</td>
              <td>${l.article_designation || '—'}</td>
              <td class="num">${pmp ? pmp.toLocaleString('fr-FR',{minimumFractionDigits:2,maximumFractionDigits:2}) : '—'}</td>
              <td class="num">${montant ? montant.toLocaleString('fr-FR',{minimumFractionDigits:2,maximumFractionDigits:2}) : '—'}</td>
            </tr>`;
          }).join('')}
          <tr class="total-row">
            <td colspan="4" style="text-align:right">Total HT</td>
            <td class="num">${total.toLocaleString('fr-FR',{minimumFractionDigits:2,maximumFractionDigits:2})} €</td>
          </tr>
        </tbody>
      </table>
      <script>window.onload=()=>window.print();<\/script>
    </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  }

  function setEdit(ligneId, key, val) {
    setLigneEdits(prev => ({ ...prev, [ligneId]: { ...prev[ligneId], [key]: val } }));
  }

  // ── Édition demande ───────────────────────────────────────────────────────
  async function startEdit() {
    if (!agences.length) {
      try {
        const r = await api.getAgences?.();
        setAgences(r?.data?.items || r?.data || []);
      } catch { setAgences([]); }
    }
    setEditForm({
      agence_source_id: doc.agence_source_id,
      date_demande:     doc.date_demande ? doc.date_demande.slice(0, 10) : new Date().toISOString().slice(0, 10),
      notes:            doc.notes || '',
    });
    setEditLignes((doc.lignes || []).map((l, i) => ({
      ...l,
      _key: l.ligne_id || `k${i}`,
    })));
    setEditMode(true);
  }

  function cancelEdit() { setEditMode(false); }

  function addEditLigne() {
    setEditLignes(prev => [...prev, { article_id: null, article_ref: '', article_designation: '', article_type: '', materiel_id: null, quantite_demandee: 1, _key: Date.now() + Math.random() }]);
  }

  function removeEditLigne(key) {
    setEditLignes(prev => prev.filter(l => l._key !== key));
  }

  function updateEditLigne(key, field, val) {
    setEditLignes(prev => prev.map(l => l._key === key ? { ...l, [field]: val } : l));
  }

  async function handleSaveEdit() {
    if (!editForm.agence_source_id) { toast.error('Sélectionnez l\'agence expéditrice'); return; }
    const validLignes = editLignes.filter(l => l.article_id || l.article_designation);
    if (!validLignes.length) { toast.error('Ajoutez au moins un article'); return; }
    setSaving(true);
    try {
      const { data } = await api.updateTransfert(doc.transfert_id, {
        agence_source_id: editForm.agence_source_id,
        date_demande:     editForm.date_demande,
        notes:            editForm.notes || null,
        lignes: validLignes.map((l, i) => ({
          article_id:          l.article_id,
          article_ref:         l.article_ref,
          article_designation: l.article_designation,
          article_type:        l.article_type,
          materiel_id:         l.materiel_id || null,
          quantite_demandee:   parseFloat(l.quantite_demandee) || 1,
          sequence:            i,
        })),
      });
      setDoc(data);
      setEditMode(false);
      onRefresh();
      toast.success('Demande modifiée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur modification'); }
    setSaving(false);
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Loader2 size={20} className="animate-spin" style={{ color: '#94A3B8' }} /></div>;
  if (!doc) return null;

  const statut = doc.statut;

  return (
    <div data-testid="transfert-detail" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Header */}
      <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid #F1F5F9' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <ArrowRightLeft size={18} style={{ color: '#2563EB' }} />
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0F172A' }}>{doc.numero}</h2>
          <StatutBadge statut={doc.statut} />
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4 }}>
            <X size={16} />
          </button>
        </div>
        <div style={{ fontSize: 12, color: '#64748B', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span>{doc.agence_source_nom} → {doc.agence_dest_nom}</span>
          <span>·</span>
          <span>Demande : {fmtDate(doc.date_demande || doc.created_at)}</span>
          {doc.date_expedition && <><span>·</span><span>Expédié : {fmtDate(doc.date_expedition)}</span></>}
          {doc.date_reception && <><span>·</span><span>Reçu : {fmtDate(doc.date_reception)}</span></>}
        </div>
      </div>

      {/* Corps — lignes */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 24px' }}>

        {/* Notes */}
        {doc.notes && (
          <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#92400E' }}>
            {doc.notes}
          </div>
        )}

        {/* Tableau lignes */}
        {(doc.lignes || []).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#CBD5E1', fontSize: 13 }}>Aucune ligne</div>
        ) : mode === 'expedier' && statut === 'demande' ? (
          /* ── Panel Expédition ── */
          (() => {
            const hasMateriel = (doc.lignes || []).some(l => l.article_type === 'materiel');
            return (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                    <th style={TH_STYLE}>Référence</th>
                    <th style={TH_STYLE}>Désignation</th>
                    <th style={{ ...TH_STYLE, textAlign: 'center' }}>Qté dem.</th>
                    <th style={{ ...TH_STYLE, textAlign: 'center' }}>Qté à expédier</th>
                    {hasMateriel && <th style={TH_STYLE}>N° Série</th>}
                  </tr>
                </thead>
                <tbody>
                  {(doc.lignes || []).map(l => {
                    const isMateriel = l.article_type === 'materiel';
                    const options = materielOptions[l.ligne_id] || [];
                    const selectedMat = ligneEdits[l.ligne_id]?.materiel_id || '';
                    return (
                      <tr key={l.ligne_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ ...TD_STYLE, color: '#64748B' }}>{l.article_ref || '—'}</td>
                        <td style={TD_STYLE}>{l.article_designation || '—'}</td>
                        <td style={{ ...TD_STYLE, textAlign: 'center' }}>{l.quantite_demandee}</td>
                        <td style={{ ...TD_STYLE, textAlign: 'center' }}>
                          <input type="number" min="0" step="any"
                            value={ligneEdits[l.ligne_id]?.quantite_envoyee ?? l.quantite_demandee}
                            onChange={e => setEdit(l.ligne_id, 'quantite_envoyee', e.target.value)}
                            style={{ width: 70, border: '1px solid #E2E8F0', borderRadius: 5, padding: '3px 6px', textAlign: 'center', fontSize: 13, outline: 'none' }}
                          />
                        </td>
                        {hasMateriel && (
                          <td style={{ ...TD_STYLE }}>
                            {isMateriel ? (
                              options.length === 0 ? (
                                <span style={{ fontSize: 11, color: '#94A3B8', fontStyle: 'italic' }}>Aucun matériel dans cette agence</span>
                              ) : (
                                <select
                                  value={selectedMat}
                                  onChange={e => setEdit(l.ligne_id, 'materiel_id', e.target.value || null)}
                                  style={{ width: '100%', minWidth: 180, border: '1px solid #E2E8F0', borderRadius: 5, padding: '4px 8px', fontSize: 12, background: '#fff', outline: 'none', color: selectedMat ? '#0F172A' : '#94A3B8' }}
                                >
                                  <option value="">— Sélectionner —</option>
                                  {options.map(m => (
                                    <option key={m.materiel_id} value={m.materiel_id}>
                                      {m.numero_parc || m.numero_serie || m.nom || m.materiel_id}
                                      {m.numero_serie && m.numero_parc ? ` · SN: ${m.numero_serie}` : ''}
                                    </option>
                                  ))}
                                </select>
                              )
                            ) : (
                              <span style={{ color: '#CBD5E1' }}>—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()
        ) : mode === 'expedier' && statut === 'expedie' ? (
          /* ── Panel Expédition confirmée (après validation) ── */
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                <th style={{ ...TH_STYLE, textAlign: 'center', width: 60 }}>Qté</th>
                <th style={TH_STYLE}>Référence</th>
                <th style={TH_STYLE}>Désignation</th>
                <th style={{ ...TH_STYLE, textAlign: 'right' }}>PMP (€)</th>
                <th style={{ ...TH_STYLE, textAlign: 'right' }}>Montant (€)</th>
              </tr>
            </thead>
            <tbody>
              {(doc.lignes || []).map(l => {
                const qty = parseFloat(l.quantite_envoyee || l.quantite_demandee || 0);
                const pmp = parseFloat(l.pmp_source || 0);
                const montant = qty * pmp;
                return (
                  <tr key={l.ligne_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <td style={{ ...TD_STYLE, textAlign: 'center', fontWeight: 600 }}>{qty}</td>
                    <td style={{ ...TD_STYLE, color: '#64748B' }}>{l.article_ref || '—'}</td>
                    <td style={TD_STYLE}>{l.article_designation || '—'}</td>
                    <td style={{ ...TD_STYLE, textAlign: 'right' }}>{pmp ? pmp.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) : '—'}</td>
                    <td style={{ ...TD_STYLE, textAlign: 'right', fontWeight: 600 }}>{montant ? montant.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) : '—'}</td>
                  </tr>
                );
              })}
              {(() => {
                const total = (doc.lignes || []).reduce((s, l) => s + parseFloat(l.quantite_envoyee || l.quantite_demandee || 0) * parseFloat(l.pmp_source || l.article_prix_achat || 0), 0);
                return (
                  <tr style={{ borderTop: '2px solid #E2E8F0', background: '#F8FAFC' }}>
                    <td colSpan={4} style={{ ...TD_STYLE, textAlign: 'right', fontWeight: 700 }}>Total valorisé HT</td>
                    <td style={{ ...TD_STYLE, textAlign: 'right', fontWeight: 700 }}>{total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        ) : statut === 'expedie' && mode === 'ouvrir' ? (
          /* ── Panel lecture seule expédié (clic ligne — vue neutre) ── */
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                <th style={{ ...TH_STYLE, textAlign: 'center', width: 60 }}>Qté</th>
                <th style={TH_STYLE}>Référence</th>
                <th style={TH_STYLE}>Désignation</th>
                <th style={{ ...TH_STYLE, textAlign: 'right' }}>PMP (€)</th>
                <th style={{ ...TH_STYLE, textAlign: 'right' }}>Montant (€)</th>
              </tr>
            </thead>
            <tbody>
              {(doc.lignes || []).map(l => {
                const qty = parseFloat(l.quantite_envoyee || l.quantite_demandee || 0);
                const pmp = parseFloat(l.pmp_source || l.article_prix_achat || 0);
                const montant = qty * pmp;
                return (
                  <tr key={l.ligne_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <td style={{ ...TD_STYLE, textAlign: 'center', fontWeight: 600 }}>{qty}</td>
                    <td style={{ ...TD_STYLE, color: '#64748B' }}>{l.article_ref || '—'}</td>
                    <td style={TD_STYLE}>{l.article_designation || '—'}</td>
                    <td style={{ ...TD_STYLE, textAlign: 'right' }}>{pmp ? pmp.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) : '—'}</td>
                    <td style={{ ...TD_STYLE, textAlign: 'right', fontWeight: 600 }}>{montant ? montant.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : mode === 'receptionner' && statut === 'expedie' ? (
          /* ── Panel Réception (clic bouton [Réceptionner] uniquement) ── */
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                <th style={TH_STYLE}>Référence</th>
                <th style={TH_STYLE}>Désignation</th>
                <th style={{ ...TH_STYLE, textAlign: 'center' }}>Qté envoyée</th>
                <th style={{ ...TH_STYLE, textAlign: 'center' }}>Qté reçue</th>
                <th style={TH_STYLE}>Zone de rangement</th>
              </tr>
            </thead>
            <tbody>
              {(doc.lignes || []).map(l => (
                <tr key={l.ligne_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ ...TD_STYLE, color: '#64748B' }}>{l.article_ref || '—'}</td>
                  <td style={TD_STYLE}>{l.article_designation || '—'}</td>
                  <td style={{ ...TD_STYLE, textAlign: 'center', fontWeight: 600 }}>{l.quantite_envoyee ?? '—'}</td>
                  <td style={{ ...TD_STYLE, textAlign: 'center' }}>
                    <input type="number" min="0" step="any"
                      value={ligneEdits[l.ligne_id]?.quantite_recue ?? l.quantite_envoyee ?? l.quantite_demandee}
                      onChange={e => setEdit(l.ligne_id, 'quantite_recue', e.target.value)}
                      style={{ width: 70, border: '1px solid #E2E8F0', borderRadius: 5, padding: '3px 6px', textAlign: 'center', fontSize: 13, outline: 'none' }}
                    />
                  </td>
                  <td style={{ ...TD_STYLE, color: '#64748B', fontSize: 11 }}>
                    {l.zone_dest_nom || l.zone_dest_id
                      ? <span>{l.zone_dest_nom || l.zone_dest_id}</span>
                      : <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>Selon fiche article</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : statut === 'receptionne' ? (
          /* ── Panel Résumé complet ── */
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                <th style={TH_STYLE}>Référence</th>
                <th style={TH_STYLE}>Désignation</th>
                <th style={{ ...TH_STYLE, textAlign: 'center' }}>Qté dem.</th>
                <th style={{ ...TH_STYLE, textAlign: 'center' }}>Qté envoyée</th>
                <th style={{ ...TH_STYLE, textAlign: 'center' }}>Qté reçue</th>
              </tr>
            </thead>
            <tbody>
              {(doc.lignes || []).map(l => (
                <tr key={l.ligne_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ ...TD_STYLE, color: '#64748B' }}>{l.article_ref || '—'}</td>
                  <td style={TD_STYLE}>
                    {l.article_designation || '—'}
                    {l.materiel_id && (
                      <div style={{ fontSize: 11, color: '#2563EB', marginTop: 2 }}>N° série : {l.materiel_id}</div>
                    )}
                  </td>
                  <td style={{ ...TD_STYLE, textAlign: 'center' }}>{l.quantite_demandee}</td>
                  <td style={{ ...TD_STYLE, textAlign: 'center' }}>{l.quantite_envoyee ?? '—'}</td>
                  <td style={{ ...TD_STYLE, textAlign: 'center', fontWeight: 600, color: '#15803D' }}>{l.quantite_recue ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          /* ── Panel Demande (demande, vue lecture seule) / Annulé ── */
          editMode && statut === 'demande' ? (
            /* Mode édition inline */
            <div>
              {/* Agence expéditrice */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ ...TH_STYLE, display: 'block', marginBottom: 4 }}>Demande à</label>
                <select
                  style={{ width: '100%', border: '1px solid #E2E8F0', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: '#fff', outline: 'none' }}
                  value={editForm.agence_source_id || ''}
                  onChange={e => setEditForm(f => ({ ...f, agence_source_id: e.target.value }))}
                >
                  <option value="">— Sélectionner une agence —</option>
                  {agences.filter(a => a.agence_id !== doc.agence_dest_id).map(a => (
                    <option key={a.agence_id} value={a.agence_id}>{a.nom}</option>
                  ))}
                </select>
              </div>
              {/* Date + Notes */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                <div>
                  <label style={{ ...TH_STYLE, display: 'block', marginBottom: 4 }}>Date de demande</label>
                  <input type="date"
                    style={{ width: '100%', border: '1px solid #E2E8F0', borderRadius: 7, padding: '7px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                    value={editForm.date_demande || ''}
                    onChange={e => setEditForm(f => ({ ...f, date_demande: e.target.value }))}
                  />
                </div>
                <div>
                  <label style={{ ...TH_STYLE, display: 'block', marginBottom: 4 }}>Notes</label>
                  <input
                    style={{ width: '100%', border: '1px solid #E2E8F0', borderRadius: 7, padding: '7px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                    value={editForm.notes || ''}
                    onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Informations complémentaires…"
                  />
                </div>
              </div>
              {/* Lignes */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={TH_STYLE}>Articles</span>
                <button onClick={addEditLigne} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: '1px solid #CBD5E1', borderRadius: 6, padding: '3px 9px', fontSize: 12, cursor: 'pointer', color: '#475569' }}>
                  <Plus size={12} /> Ajouter
                </button>
              </div>
              {editLignes.map((l) => (
                <div key={l._key} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px auto', gap: 8, alignItems: 'end' }}>
                    <div>
                      <label style={{ ...TH_STYLE, display: 'block', marginBottom: 3 }}>Article</label>
                      <ArticleCombobox
                        value={l}
                        onChange={art => {
                          if (art) {
                            updateEditLigne(l._key, 'article_id', art.article_id);
                            updateEditLigne(l._key, 'article_ref', art.article_ref);
                            updateEditLigne(l._key, 'article_designation', art.article_designation);
                            updateEditLigne(l._key, 'article_type', art.article_type);
                          }
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ ...TH_STYLE, display: 'block', marginBottom: 3 }}>Qté</label>
                      <input type="number" min="0" step="any"
                        style={{ width: '100%', border: '1px solid #E2E8F0', borderRadius: 7, padding: '5px 8px', fontSize: 13, outline: 'none' }}
                        value={l.quantite_demandee}
                        onChange={e => updateEditLigne(l._key, 'quantite_demandee', e.target.value)}
                      />
                    </div>
                    <button onClick={() => removeEditLigne(l._key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 4, alignSelf: 'flex-end', paddingBottom: 6 }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                  <th style={TH_STYLE}>Référence</th>
                  <th style={TH_STYLE}>Désignation</th>
                  <th style={{ ...TH_STYLE, textAlign: 'center' }}>Qté demandée</th>
                </tr>
              </thead>
              <tbody>
                {(doc.lignes || []).map(l => (
                  <tr key={l.ligne_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <td style={{ ...TD_STYLE, color: '#64748B' }}>{l.article_ref || '—'}</td>
                    <td style={TD_STYLE}>{l.article_designation || '—'}</td>
                    <td style={{ ...TD_STYLE, textAlign: 'center', fontWeight: 600 }}>{l.quantite_demandee}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Footer — mode édition */}
      {editMode && statut === 'demande' && (
        <div style={{ padding: '12px 24px', borderTop: '1px solid #F1F5F9', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={cancelEdit}
            style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: 7, padding: '6px 14px', fontSize: 13, cursor: 'pointer', color: '#64748B' }}>
            Annuler
          </button>
          <button onClick={handleSaveEdit} disabled={saving} data-testid="btn-save-edit-transfert"
            style={{ background: '#2563EB', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Save size={13} /> Sauvegarder
          </button>
        </div>
      )}

      {/* Footer — mode lecture (demande) : bouton Modifier */}
      {!editMode && statut === 'demande' && mode === 'ouvrir' && (
        <div style={{ padding: '12px 24px', borderTop: '1px solid #F1F5F9', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={handleAnnuler} disabled={saving}
            style={{ background: 'none', border: '1px solid #FCA5A5', color: '#DC2626', borderRadius: 7, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
            Annuler le transfert
          </button>
          <button onClick={startEdit} data-testid="btn-modifier-demande"
            style={{ background: '#F8FAFC', border: '1px solid #CBD5E1', color: '#374151', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Modifier
          </button>
        </div>
      )}

      {/* Footer — Expédition (saisie) */}
      {mode === 'expedier' && statut === 'demande' && (
        <div style={{ padding: '12px 24px', borderTop: '1px solid #F1F5F9', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button onClick={handleAnnuler} disabled={saving}
            style={{ background: 'none', border: '1px solid #FCA5A5', color: '#DC2626', borderRadius: 7, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
            Annuler le transfert
          </button>
          <button onClick={handleExpedier} disabled={saving} data-testid="btn-expedier"
            style={{ background: '#F59E0B', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Send size={14} /> Valider l'expédition
          </button>
        </div>
      )}
      {/* Footer — Expédition confirmée (après validation) */}
      {mode === 'expedier' && statut === 'expedie' && (
        <div style={{ padding: '12px 24px', borderTop: '1px solid #F1F5F9', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={handlePrint} data-testid="btn-imprimer-bordereau"
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid #CBD5E1', color: '#475569', borderRadius: 7, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
            <Printer size={13} /> Imprimer le bordereau
          </button>
        </div>
      )}
      {/* Footer — vue lecture seule expédié */}
      {statut === 'expedie' && mode === 'ouvrir' && (
        <div style={{ padding: '8px 24px 12px', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={handlePrint}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid #CBD5E1', color: '#475569', borderRadius: 7, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
            <Printer size={13} /> Imprimer le bordereau
          </button>
        </div>
      )}
      {/* Footer — Réception */}
      {mode === 'receptionner' && statut === 'expedie' && (
        <div style={{ padding: '12px 24px', borderTop: '1px solid #F1F5F9', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button onClick={handlePrint}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid #CBD5E1', color: '#475569', borderRadius: 7, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
            <Printer size={13} /> Imprimer le bordereau
          </button>
          <button onClick={handleReceptionner} disabled={saving} data-testid="btn-receptionner"
            style={{ background: '#16A34A', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <PackageCheck size={14} /> Valider la réception
          </button>
        </div>
      )}

      {/* Bordereau si déjà réceptionné */}
      {statut === 'receptionne' && (
        <div style={{ padding: '8px 24px 12px', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={handlePrint} data-testid="btn-imprimer-bordereau-done"
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid #CBD5E1', color: '#475569', borderRadius: 7, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
            <Printer size={13} /> Imprimer le bordereau
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Ligne détail ─────────────────────────────────────────────────────────────

function LigneDetail({ ligne: l, statut, edits, onChange, zonesSource, zonesDest }) {
  const isBrouillon      = statut === 'brouillon';
  const isExpedie        = statut === 'expedie';
  const isReceptionne    = statut === 'receptionne';

  return (
    <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#0F172A' }}>{l.article_designation || '—'}</div>
          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2, display: 'flex', gap: 8 }}>
            {l.article_ref && <span>{l.article_ref}</span>}
            {l.article_type && <span style={{ background: '#EFF6FF', color: '#2563EB', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>{l.article_type}</span>}
            {l.materiel_id && <span style={{ background: '#F0FDF4', color: '#15803D', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>SN: {l.materiel_id}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 12, color: '#64748B', flexShrink: 0 }}>
          <div>Demandé : <strong>{l.quantite_demandee}</strong></div>
        </div>
      </div>

      {/* Champs d'expédition */}
      {isBrouillon && (
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, color: '#64748B', display: 'block', marginBottom: 3 }}>Qté à expédier</label>
            <input type="number" min="0" step="any"
              value={edits.quantite_envoyee ?? l.quantite_demandee}
              onChange={e => onChange('quantite_envoyee', e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400"
            />
          </div>
        </div>
      )}

      {/* Champs de réception */}
      {isExpedie && (
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, color: '#64748B', display: 'block', marginBottom: 3 }}>Qté reçue</label>
            <input type="number" min="0" step="any"
              value={edits.quantite_recue ?? l.quantite_envoyee ?? l.quantite_demandee}
              onChange={e => onChange('quantite_recue', e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#64748B', display: 'block', marginBottom: 3 }}>Zone de destination</label>
            <ZoneSelect zones={zonesDest} value={edits.zone_dest_id} onChange={v => onChange('zone_dest_id', v)} placeholder="Zone En transit ou finale" />
          </div>
          {l.pmp_source != null && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 11, color: '#64748B', display: 'block', marginBottom: 3 }}>
                Valeur comptable (PMP source) — lecture seule
              </label>
              <div style={{
                background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6,
                padding: '6px 10px', fontSize: 13, color: '#0F172A', fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <span>{parseFloat(l.pmp_source).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} € / u</span>
                <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 400 }}>
                  Total : {(parseFloat(edits.quantite_recue ?? l.quantite_envoyee ?? l.quantite_demandee) * parseFloat(l.pmp_source)).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Résumé si réceptionné */}
      {isReceptionne && (
        <div style={{ marginTop: 8, display: 'flex', gap: 16, fontSize: 11, color: '#64748B' }}>
          {l.quantite_envoyee > 0 && <span>Envoyé : <strong>{l.quantite_envoyee}</strong></span>}
          {l.quantite_recue > 0   && <span>Reçu : <strong>{l.quantite_recue}</strong></span>}
          {l.zone_dest_id   && <span>Zone arrivée : <strong>{l.zone_dest_nom || l.zone_dest_id}</strong></span>}
        </div>
      )}
    </div>
  );
}

// ─── Formulaire de création ───────────────────────────────────────────────────

function TransfertCreateForm({ activeAgence: activeAgenceProp, onClose, onCreated }) {
  // Gel de l'agence source au moment d'ouverture du formulaire
  const [sourceAgence] = useState(() => activeAgenceProp);
  const [agences, setAgences]   = useState([]);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({
    agence_dest_id: '',
    date_demande:   new Date().toISOString().slice(0, 10),
    notes:          '',
  });
  const [lignes, setLignes] = useState([
    { article_id: null, article_ref: '', article_designation: '', article_type: '', materiel_id: null, quantite_demandee: 1, _key: Date.now() }
  ]);

  useEffect(() => {
    api.getAgences?.().then(r => setAgences(r?.data?.items || r?.data || [])).catch(() => {});
  }, []);

  function addLigne() {
    setLignes(prev => [...prev, { article_id: null, article_ref: '', article_designation: '', article_type: '', materiel_id: null, quantite_demandee: 1, _key: Date.now() + Math.random() }]);
  }

  function removeLigne(key) {
    setLignes(prev => prev.filter(l => l._key !== key));
  }

  function updateLigne(key, field, val) {
    setLignes(prev => prev.map(l => l._key === key ? { ...l, [field]: val } : l));
  }

  async function handleSave() {
    if (!form.agence_dest_id) { toast.error('Sélectionnez l\'agence destinataire'); return; }
    if (form.agence_dest_id === sourceAgence?.agence_id) { toast.error('Les deux agences doivent être différentes'); return; }
    const validLignes = lignes.filter(l => l.article_id || l.article_designation);
    if (!validLignes.length) { toast.error('Ajoutez au moins un article'); return; }

    setSaving(true);
    try {
      const payload = {
        agence_source_id: form.agence_dest_id,     // celui qui va expédier
        agence_dest_id:   sourceAgence.agence_id,  // vous, qui allez recevoir
        stock_source_id:  null,
        stock_dest_id:    null,
        date_demande:     form.date_demande,
        notes:            form.notes,
        lignes: validLignes.map((l, i) => ({
          article_id:          l.article_id,
          article_ref:         l.article_ref,
          article_designation: l.article_designation,
          article_type:        l.article_type,
          materiel_id:         l.materiel_id || null,
          quantite_demandee:   parseFloat(l.quantite_demandee) || 1,
          sequence:            i,
        })),
      };
      const { data } = await api.creerTransfert(payload);
      onCreated(data.transfert_id);
      toast.success(`Transfert ${data.numero} créé`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur création'); }
    setSaving(false);
  }

  const agencesDest = agences.filter(a => a.agence_id !== sourceAgence?.agence_id);
  const inputStyle = { width: '100%', border: '1px solid #E2E8F0', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: '#fff', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <div data-testid="transfert-create-form" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Header */}
      <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 10 }}>
        <ArrowRightLeft size={18} style={{ color: '#2563EB' }} />
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0F172A' }}>Nouvelle demande de transfert</h2>
        <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4 }}>
          <X size={16} />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

        {/* Demande à */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Demande à</label>
          <select style={inputStyle} value={form.agence_dest_id} onChange={e => setForm(f => ({ ...f, agence_dest_id: e.target.value }))}>
            <option value="">— Sélectionner une agence —</option>
            {agencesDest.map(a => <option key={a.agence_id} value={a.agence_id}>{a.nom}</option>)}
          </select>
        </div>

        {/* Date + Notes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Date de demande</label>
            <input type="date" style={inputStyle} value={form.date_demande} onChange={e => setForm(f => ({ ...f, date_demande: e.target.value }))} />
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <input style={inputStyle} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Informations complémentaires…" />
          </div>
        </div>

        {/* Lignes articles */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={labelStyle}>Articles</span>
          <button onClick={addLigne} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: '1px solid #CBD5E1', borderRadius: 6, padding: '3px 9px', fontSize: 12, cursor: 'pointer', color: '#475569' }}>
            <Plus size={12} /> Ajouter
          </button>
        </div>

        {lignes.map((l, i) => (
          <div key={l._key} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px auto', gap: 8, alignItems: 'end' }}>
              <div>
                <label style={{ ...labelStyle, marginBottom: 3 }}>Article</label>
                <ArticleCombobox
                  value={l}
                  onChange={art => {
                    if (art) updateLigne(l._key, 'article_id', art.article_id);
                    if (art) updateLigne(l._key, 'article_ref', art.article_ref);
                    if (art) updateLigne(l._key, 'article_designation', art.article_designation);
                    if (art) updateLigne(l._key, 'article_type', art.article_type);
                  }}
                />
              </div>
              <div>
                <label style={{ ...labelStyle, marginBottom: 3 }}>Qté</label>
                <input type="number" min="0" step="any" style={{ ...inputStyle }}
                  value={l.quantite_demandee}
                  onChange={e => updateLigne(l._key, 'quantite_demandee', e.target.value)}
                />
              </div>
              <button onClick={() => removeLigne(l._key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: '4px', alignSelf: 'flex-end', paddingBottom: 6 }}>
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 24px', borderTop: '1px solid #F1F5F9', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: 7, padding: '7px 16px', fontSize: 13, cursor: 'pointer', color: '#64748B' }}>
          Annuler
        </button>
        <button onClick={handleSave} disabled={saving} data-testid="btn-save-transfert"
          style={{ background: '#2563EB', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Save size={13} /> Créer la demande
        </button>
      </div>
    </div>
  );
}
